/**
 * DID inventory management page: stats roll-up, filtered list, and
 * add/assign/history/delete actions. Backed by /api/v1/dids/*.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { didsApi } from "../../api/dids";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import AddDIDDialog from "../../components/dids/AddDIDDialog";
import AssignDIDDialog from "../../components/dids/AssignDIDDialog";
import AssignmentHistoryDialog from "../../components/dids/AssignmentHistoryDialog";
import type { DID, DIDInventoryStats, DIDStatus } from "../../types/did";

const PAGE_SIZE = 100;

const STATUS_COLORS: Record<DIDStatus, "success" | "primary" | "warning" | "default"> = {
  available: "success",
  assigned: "primary",
  reserved: "warning",
  unavailable: "default",
};

/** DID inventory page. */
export default function DIDInventory() {
  const [dids, setDids] = useState<DID[]>([]);
  const [stats, setStats] = useState<DIDInventoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [areaCodeFilter, setAreaCodeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<DIDStatus | "">("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<DID | null>(null);
  const [historyTarget, setHistoryTarget] = useState<DID | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DID | null>(null);

  const fetchDids = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      setError("");
      try {
        const response = await didsApi.list({
          area_code: areaCodeFilter.trim() || undefined,
          status_filter: statusFilter || undefined,
          customer_id: customerFilter.trim() || undefined,
          page: targetPage + 1,
          page_size: PAGE_SIZE,
        });
        setDids(response.data);
        // Backend returns a plain list without a total; detect a next page
        // by asking for one row more than displayed.
        setHasMore(response.data.length === PAGE_SIZE);
        setPage(targetPage);
      } catch {
        setError("Failed to load DID inventory.");
      } finally {
        setLoading(false);
      }
    },
    [areaCodeFilter, statusFilter, customerFilter],
  );

  const fetchStats = useCallback(async () => {
    try {
      const response = await didsApi.stats();
      setStats(response.data);
    } catch {
      // Stats are informational; list errors are surfaced separately.
    }
  }, []);

  useEffect(() => {
    void fetchDids(0);
  }, [fetchDids]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  const showSuccess = (message: string) => {
    setSuccess(message);
    void fetchDids(page);
    void fetchStats();
  };

  const handleDeleteConfirmed = async () => {
    if (!deleteTarget) {
      return;
    }
    try {
      await didsApi.remove(deleteTarget.id);
      showSuccess(`DID ${deleteTarget.phone_number} deleted.`);
    } catch {
      setError(
        "Failed to delete DID. Release any assignment first, then retry.",
      );
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">DID Inventory</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add DID
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Total
                </Typography>
                <Typography variant="h6">{stats.total_dids}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Available
                </Typography>
                <Typography variant="h6">{stats.available_dids}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Assigned
                </Typography>
                <Typography variant="h6">{stats.assigned_dids}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Reserved
                </Typography>
                <Typography variant="h6">{stats.reserved_dids}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Unavailable
                </Typography>
                <Typography variant="h6">{stats.unavailable_dids}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="body2">
                  Monthly revenue
                </Typography>
                <Typography variant="h6">
                  ${stats.monthly_revenue.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Area code"
          size="small"
          value={areaCodeFilter}
          onChange={(e) => setAreaCodeFilter(e.target.value)}
          placeholder="212"
        />
        <TextField
          select
          label="Status"
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as DIDStatus | "")}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="available">Available</MenuItem>
          <MenuItem value="assigned">Assigned</MenuItem>
          <MenuItem value="reserved">Reserved</MenuItem>
          <MenuItem value="unavailable">Unavailable</MenuItem>
        </TextField>
        <TextField
          label="Customer ID"
          size="small"
          value={customerFilter}
          onChange={(e) => setCustomerFilter(e.target.value)}
          placeholder="acct-123"
        />
      </Stack>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Phone number</TableCell>
              <TableCell>Area code</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Monthly cost</TableCell>
              <TableCell>Assigned to</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dids.map((did) => (
              <TableRow key={did.id} hover>
                <TableCell>{did.phone_number}</TableCell>
                <TableCell>{did.area_code}</TableCell>
                <TableCell>
                  <Chip
                    label={did.status}
                    color={STATUS_COLORS[did.status] ?? "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  ${did.monthly_cost.toFixed(2)}
                </TableCell>
                <TableCell>{did.assigned_customer_id ?? "—"}</TableCell>
                <TableCell align="right">
                  <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                  >
                    <Button
                      size="small"
                      onClick={() => setAssignTarget(did)}
                    >
                      {did.status === "assigned" ? "Reassign" : "Assign"}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setHistoryTarget(did)}
                    >
                      History
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(did)}
                    >
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!loading && dids.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    No DIDs match the current filters. Add one to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TablePagination
            component="div"
            count={-1}
            page={page}
            onPageChange={(_, newPage) => void fetchDids(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            labelDisplayedRows={({ from, to }) => `DIDs ${from}–${to}`}
            slotProps={{
              actions: {
                previousButton: { disabled: page === 0 },
                nextButton: { disabled: !hasMore },
              },
            }}
          />
        </Table>
      </TableContainer>

      <AddDIDDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={showSuccess}
      />
      <AssignDIDDialog
        open={assignTarget !== null}
        did={assignTarget}
        onClose={() => setAssignTarget(null)}
        onAssigned={showSuccess}
      />
      <AssignmentHistoryDialog
        open={historyTarget !== null}
        didId={historyTarget?.id ?? null}
        phoneNumber={historyTarget?.phone_number ?? ""}
        onClose={() => setHistoryTarget(null)}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete DID"
        message={`Delete DID ${deleteTarget?.phone_number ?? ""}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirmed}
        onClose={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
